import { useCallback, useEffect, useState } from "react";
import { Box } from "@primer/react";
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Pie, Bar } from 'react-chartjs-2';
import { foDangKyAnHocSinhApi } from "../../api/foDangKyAnHocSinhApi";
import { useCommonSelectedHook } from "../../hooks/useCommonSelectedHook";
import ComboboxDotDangKyAn from "../../components/combobox-dot-dang-ky-an/ComboboxDotDangKyAn";
import MyText from "../../components/ui/text";
import MyTextInput from "../../components/ui/text-input";
import dayjs from "dayjs";

// Đăng ký các components của Chart.js
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title, ChartDataLabels);

interface IDashboardData {
  cas: number;
  kas: number;
  ka: number;
  total_dang_ky: number;
  total_xac_nhan: number;
  total_xac_nhan_ka: number;
}

const DashBoardPage = () => {
  const { dm_coso_id, nam_hoc } = useCommonSelectedHook();

  const [dashboardData, setDashboardData] = useState<IDashboardData>({
    cas: 0,
    kas: 0,
    ka: 0,
    total_dang_ky: 0,
    total_xac_nhan: 0,
    total_xac_nhan_ka: 0
  });

  const [filterDotDangKyId, setFilterDotDangKyId] = useState<number>(0);
  const [fromDate, setFromDate] = useState<string>(dayjs().subtract(1, "day").format("YYYY-MM-DD"));
  const [toDate, setToDate] = useState<string>(dayjs().subtract(1, "day").format("YYYY-MM-DD"));
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [dailyData, setDailyData] = useState<any[]>([]);

  const loadDashboardData = useCallback(async () => {
    if (!dm_coso_id || !nam_hoc || !fromDate || !toDate) return;

    setIsLoading(true);
    try {
      const res = await foDangKyAnHocSinhApi.select_by_baocao_thang({
        dm_coso_id,
        dm_truong_id: 0, // Lấy tất cả trường
        fo_dotdangky_an_id: filterDotDangKyId,
        nam_hoc,
        from_date: fromDate,
        to_date: toDate,
      });

      if (res.is_success) {
        const rawData = res.data || [];

        // Tính toán thống kê tổng
        let cas = 0, kas = 0, ka = 0;
        const uniqueRegistrations = new Set();
        const uniqueConfirmations = new Set();
        const uniqueNotConfirmations = new Set();

        // Tính toán thống kê theo ngày
        const dailyStats: { [key: string]: { dang_ky: number; xac_nhan: number; xac_nhan_ka: number } } = {};

        rawData.forEach((record: any) => {
          // Đếm combo ăn tổng
          switch (record.fo_xacnhanan_combo_id) {
            case 1:
              cas++;
              break;
            case 2:
              kas++;
              break;
            case 3:
              ka++;
              break;
          }

          // Đếm số lượng đăng ký và xác nhận tổng
          const registrationKey = `${record.ts_hocsinh_id}_${record.ngay_dang_ky}`;
          uniqueRegistrations.add(registrationKey);

          if (record.fo_xacnhanan_combo_id === 1 || record.fo_xacnhanan_combo_id === 2) {
            uniqueConfirmations.add(registrationKey);
          }

          if (record.fo_xacnhanan_combo_id === 3) {
            uniqueNotConfirmations.add(registrationKey);
          }

          // Thống kê theo ngày
          const dateKey = dayjs(record.ngay_dang_ky).format("DD/MM");
          if (!dailyStats[dateKey]) {
            dailyStats[dateKey] = { dang_ky: 0, xac_nhan: 0, xac_nhan_ka: 0 };
          }

          // Đếm đăng ký theo ngày (tất cả combo)
          dailyStats[dateKey].dang_ky++;

          // Đếm xác nhận theo ngày (chỉ combo 1 và 2)
          if (record.fo_xacnhanan_combo_id === 1 || record.fo_xacnhanan_combo_id === 2) {
            dailyStats[dateKey].xac_nhan++;
          }

          // Đếm xác nhận không ăn theo ngày (chỉ combo 3)
          if (record.fo_xacnhanan_combo_id === 3) {
            dailyStats[dateKey].xac_nhan_ka++;
          }
        });

        // Chuyển đổi thành mảng cho biểu đồ
        const dailyArray = Object.entries(dailyStats)
          .map(([date, stats]) => ({
            date,
            dang_ky: stats.dang_ky,
            xac_nhan: stats.xac_nhan,
            xac_nhan_ka: stats.xac_nhan_ka
          }))
          .sort((a, b) => {
            // Sắp xếp theo ngày
            const dateA = dayjs(a.date, "DD/MM");
            const dateB = dayjs(b.date, "DD/MM");
            return dateA.isBefore(dateB) ? -1 : 1;
          });

        setDailyData(dailyArray);
        setDashboardData({
          cas,
          kas,
          ka,
          total_dang_ky: uniqueRegistrations.size,
          total_xac_nhan: uniqueConfirmations.size,
          total_xac_nhan_ka: uniqueNotConfirmations.size
        });
      }
    } catch (error) {
      console.error("Error loading dashboard data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [dm_coso_id, nam_hoc, fromDate, toDate, filterDotDangKyId]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // Dữ liệu cho biểu đồ tròn - Tỉ lệ combo ăn
  const pieChartData = {
    labels: ['Có ăn sáng (CAS)', 'Không ăn sáng (KAS)', 'Không ăn (KA)'],
    datasets: [
      {
        data: [dashboardData.cas, dashboardData.kas, dashboardData.ka],
        backgroundColor: [
          '#4BC0C0', // Xanh lam cho CAS
          '#FFCE56', // Vàng cho KA
          '#FF6384', // Đỏ cho KAS
        ],
        borderColor: [
          '#4BC0C0',
          '#FFCE56',
          '#FF6384',
        ],
        borderWidth: 2,
      },
    ],
  };

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          font: {
            size: 12,
          },
          padding: 10,
        },
      },
      title: {
        display: true,
        text: 'Tỉ lệ đăng ký ca ăn',
        font: {
          size: 14,
          weight: 'bold' as const,
        },
        padding: {
          bottom: 10,
        },
      },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
            const percentage = total > 0 ? ((context.parsed * 100) / total).toFixed(1) : '0';
            return `${context.label}: ${context.parsed} (${percentage}%)`;
          }
        }
      },
      datalabels: {
        display: true,
        color: 'white',
        font: {
          size: 14,
          weight: 'bold' as const,
        },
        formatter: (value: number, context: any) => {
          const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
          const percentage = total > 0 ? ((value * 100) / total).toFixed(1) : '0';
          return value > 0 ? `${value}\n(${percentage}%)` : '';
        },
        textAlign: 'center' as const,
      }
    },
  };

  // Dữ liệu cho biểu đồ cột - Đăng ký vs Xác nhận theo ngày
  const barChartData = {
    labels: dailyData.map(item => item.date),
    datasets: [
      {
        label: 'Tổng đăng ký',
        data: dailyData.map(item => item.dang_ky),
        backgroundColor: '#60a5fa', // Xanh dương vừa phải
        borderColor: '#2563eb',
        borderWidth: 2,
      },
      {
        label: 'Xác nhận ăn',
        data: dailyData.map(item => item.xac_nhan),
        backgroundColor: '#a78bfa', // Tím vừa phải
        borderColor: '#7c3aed',
        borderWidth: 2,
      },
      {
        label: 'Xác nhận không ăn',
        data: dailyData.map(item => item.xac_nhan_ka),
        backgroundColor: '#FF6384', // Tím vừa phải
        borderColor: '#FF6384',
        borderWidth: 2,
      },
    ],
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          font: {
            size: 12,
          },
          padding: 10,
        },
      },
      title: {
        display: true,
        text: 'Đăng ký và xác nhận ăn theo ngày',
        font: {
          size: 14,
          weight: 'bold' as const,
        },
        padding: {
          bottom: 10,
        },
      },
      datalabels: {
        display: true,
        color: 'white',
        font: {
          size: 12,
          weight: 'bold' as const,
        },
        formatter: (value: number) => {
          return value > 0 ? value.toString() : '';
        },
        anchor: 'center' as const,
        align: 'center' as const,
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
          font: {
            size: 11,
          },
        },
        title: {
          display: true,
          text: 'Số lượng',
          font: {
            size: 12,
          },
        },
      },
      x: {
        title: {
          display: true,
          text: 'Ngày',
          font: {
            size: 12,
          },
        },
        ticks: {
          font: {
            size: 11,
          },
        },
      },
    },
  };

  return (
    <Box sx={{ padding: 3 }}>
      <MyText
        text="Dashboard"
        sx={{
          fontSize: 24,
          fontWeight: 'bold',
          mb: 3,
          color: 'fg.default'
        }}
      />

      {/* Bộ lọc */}
      <Box sx={{
        display: "flex",
        gap: 2,
        flexWrap: "wrap",
        mb: 4,
        p: 3,
        border: "1px solid",
        borderColor: "border.default",
        borderRadius: 2,
        bg: "canvas.subtle"
      }}>
        <Box>
          <MyText text="Từ ngày " sx={{ mb: 1, fontWeight: "600", fontSize: "13px", color: "fg.default" }} />
          <MyTextInput
            type="date"
            sx={{
              width: "180px",
              height: "30px",
              borderRadius: "6px",
              border: "1px solid",
              borderColor: "border.default",
              "&:focus": {
                borderColor: "accent.emphasis",
                boxShadow: "0 0 0 2px rgba(3, 102, 214, 0.2)"
              }
            }}
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </Box>

        <Box>
          <MyText text="Đến ngày " sx={{ mb: 1, fontWeight: "600", fontSize: "13px", color: "fg.default" }} />
          <MyTextInput
            type="date"
            sx={{
              width: "180px",
              height: "30px",
              borderRadius: "6px",
              border: "1px solid",
              borderColor: "border.default",
              "&:focus": {
                borderColor: "accent.emphasis",
                boxShadow: "0 0 0 2px rgba(3, 102, 214, 0.2)"
              }
            }}
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </Box>

        <Box>
          <ComboboxDotDangKyAn
            value={filterDotDangKyId}
            onValueChanged={setFilterDotDangKyId}
            isShowClearButton
            isShowAll
          />
        </Box>
      </Box>

      {/* Thống kê số liệu */}
      <div style={{
        marginBottom: '20px',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px'
      }}>
        <Box sx={{
          flex: '1 1 120px',
          p: 1.5,
          border: "1px solid",
          borderColor: "border.default",
          borderRadius: 2,
          bg: "success.subtle",
          textAlign: 'center'
        }}>
          <MyText text="CAS - Có ăn sáng" sx={{ fontSize: 11, fontWeight: 'bold', color: 'success.fg', display: 'block', mb: 0.5 }} />
          <MyText text={dashboardData.cas.toString()} sx={{ fontSize: 16, fontWeight: 'bold', color: 'success.emphasis' }} />
        </Box>

        <Box sx={{
          flex: '1 1 120px',
          p: 1.5,
          border: "1px solid",
          borderColor: "border.default",
          borderRadius: 2,
          bg: "attention.subtle",
          textAlign: 'center'
        }}>
          <MyText text="KAS - Không ăn sáng" sx={{ fontSize: 11, fontWeight: 'bold', color: 'attention.fg', display: 'block', mb: 0.5 }} />
          <MyText text={dashboardData.kas.toString()} sx={{ fontSize: 16, fontWeight: 'bold', color: 'attention.emphasis' }} />
        </Box>

        <Box sx={{
          flex: '1 1 120px',
          p: 1.5,
          border: "1px solid",
          borderColor: "border.default",
          borderRadius: 2,
          bg: "danger.subtle",
          textAlign: 'center'
        }}>
          <MyText text="Không ăn - KA" sx={{ fontSize: 11, fontWeight: 'bold', color: 'danger.fg', display: 'block', mb: 0.5 }} />
          <MyText text={dashboardData.ka.toString()} sx={{ fontSize: 16, fontWeight: 'bold', color: 'danger.emphasis' }} />
        </Box>

        <Box sx={{
          flex: '1 1 120px',
          p: 1.5,
          border: "1px solid",
          borderColor: "border.default",
          borderRadius: 2,
          bg: "accent.subtle",
          textAlign: 'center'
        }}>
          <MyText text="Tổng đăng ký" sx={{ fontSize: 11, fontWeight: 'bold', color: 'accent.fg', display: 'block', mb: 0.5 }} />
          <MyText text={dashboardData.total_dang_ky.toString()} sx={{ fontSize: 16, fontWeight: 'bold', color: 'accent.emphasis' }} />
        </Box>

        <Box sx={{
          flex: '1 1 120px',
          p: 1.5,
          border: "1px solid",
          borderColor: "border.default",
          borderRadius: 2,
          bg: "done.subtle",
          textAlign: 'center'
        }}>
          <MyText text="Xác nhận ăn" sx={{ fontSize: 11, fontWeight: 'bold', color: 'done.fg', display: 'block', mb: 0.5 }} />
          <MyText text={dashboardData.total_xac_nhan.toString()} sx={{ fontSize: 16, fontWeight: 'bold', color: 'done.emphasis' }} />
        </Box>
         <Box sx={{
          flex: '1 1 120px',
          p: 1.5,
          border: "1px solid",
          borderColor: "border.default",
          borderRadius: 2,
          bg: "done.subtle",
          textAlign: 'center'
        }}>
          <MyText text="Xác nhận không ăn" sx={{ fontSize: 11, fontWeight: 'bold', color: 'danger.fg', display: 'block', mb: 0.5 }} />
          <MyText text={dashboardData.total_xac_nhan_ka.toString()} sx={{ fontSize: 16, fontWeight: 'bold', color: 'danger.emphasis' }} />
        </Box>
      </div>

      {isLoading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <MyText text="Đang tải dữ liệu..." />
        </Box>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'row', gap: '16px', width: '100%' }}>
          {/* Biểu đồ tròn */}
          <Box sx={{
            flex: 1,
            height: 'calc(100vh - 320px)',
            minHeight: '450px',
            maxHeight: '600px',
            p: 2,
            border: "1px solid",
            borderColor: "border.default",
            borderRadius: 2,
            bg: "canvas.default"
          }}>
            <div style={{ width: '100%', height: '100%' }}>
              <Pie data={pieChartData} options={pieChartOptions} />
            </div>
          </Box>

          {/* Biểu đồ cột */}
          <Box sx={{
            flex: 1,
            height: 'calc(100vh - 320px)',
            minHeight: '450px',
            maxHeight: '600px',
            p: 2,
            border: "1px solid",
            borderColor: "border.default",
            borderRadius: 2,
            bg: "canvas.default"
          }}>
            <div style={{ width: '100%', height: '100%' }}>
              <Bar data={barChartData} options={barChartOptions} />
            </div>
          </Box>
        </div>
      )}
    </Box>
  );
};

export default DashBoardPage;